import React, { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon, RefreshIcon, DownloadIcon } from '@heroicons/react/outline';
import { ClockIcon, CheckCircleIcon } from '@heroicons/react/solid';
import { functions } from '../../../firebase';
import { formatDateTime } from '../../../utils/timestampUtils';
import { httpsCallable } from 'firebase/functions';
import { isSectionVisibleForRole } from '../../utils/schemaEvaluator';

/**
 * RmsFormDetailsDialog
 * --------------------
 * Purpose:
 *   Displays the raw questions, answer options, and given answers for an RMS form.
 *   Used by RMS Clinicians to review form submissions without needing to navigate
 *   to the actual form page.
 *
 * Props:
 *   - isOpen: boolean - Controls dialog visibility
 *   - onClose: () => void - Callback to close the dialog
 *   - formKey: string - The form key (e.g., 'waysOfConnecting')
 *   - formTitle: string - Display title for the form
 *   - formInstance: object - The form instance data (answers, progress, status, etc.)
 *   - orgId: string - Organisation ID
 *   - patientRecordId: string - Patient record ID
 *   - role: string - 'school_user' or 'guardian'
 *   - onDownload: () => void - Optional; when given, a "Download PDF" button
 *     downloads this form's responses (only exportable forms pass one)
 *   - downloading: boolean - This form's PDF is being prepared
 *   - downloadDisabled: boolean - Another download is in progress
 *
 * Data Flow:
 *   1. Fetches the form schema using the schemaVersion from the form instance
 *   2. Matches answers from formInstance.answers against schema questions
 *   3. Displays questions grouped by section with answer highlighting
 */

// Get display value for an answer based on question type
const formatAnswer = (answer, question, allAnswers) => {
  const qtype = question?.type || 'text';

  // Group questions have no answer under their own questionId - each child is
  // stored flat in the answers map (e.g. lg_name -> lg_first_name/lg_last_name),
  // so resolve the children before the empty-answer check below.
  if (qtype === 'group') {
    const children = question?.children || [];
    const lines = children
      .map((child) => {
        const childAnswer = allAnswers ? allAnswers[child.questionId] : undefined;
        if (childAnswer === null || childAnswer === undefined || childAnswer === '') return null;
        const formatted = formatAnswer(childAnswer, child, allAnswers);
        return `• ${child.label}: ${formatted.display}`;
      })
      .filter(Boolean);
    if (lines.length === 0) {
      return { display: 'No answer given', hasAnswer: false };
    }
    return { display: lines.join('\n'), hasAnswer: true };
  }

  if (answer === null || answer === undefined || answer === '') {
    return { display: 'No answer given', hasAnswer: false };
  }

  const options = question?.options || [];

  // For radio/select questions - map optionId to label. Binary 'checkbox'
  // questions (e.g. the HFP sensory modality selectors) persist the same
  // single-selection shape, so they format identically.
  if (qtype === 'radio' || qtype === 'select' || qtype === 'checkbox') {
    if (typeof answer === 'string') {
      const opt = options.find((o) => o.optionId === answer);
      return { display: opt?.label || answer, hasAnswer: true, optionId: answer };
    }
    if (typeof answer === 'object' && answer.optionId) {
      const opt = options.find((o) => o.optionId === answer.optionId);
      let display = opt?.label || answer.optionId;
      if (answer.otherText) {
        display += `: ${answer.otherText}`;
      }
      return { display, hasAnswer: true, optionId: answer.optionId };
    }
  }

  // For checkboxes - map array of optionIds to labels
  if (qtype === 'checkboxes') {
    if (Array.isArray(answer)) {
      const labels = answer.map((a) => {
        if (typeof a === 'string') {
          const opt = options.find((o) => o.optionId === a);
          return opt?.label || a;
        }
        if (typeof a === 'object' && a.optionId) {
          const opt = options.find((o) => o.optionId === a.optionId);
          let label = opt?.label || a.optionId;
          if (a.otherText) {
            label += `: ${a.otherText}`;
          }
          return label;
        }
        return String(a);
      });
      return {
        display: labels.map((l) => `• ${l}`).join('\n'),
        hasAnswer: labels.length > 0,
        selectedIds: answer.map((a) => (typeof a === 'string' ? a : a.optionId)),
      };
    }
  }

  // For milestone groups - display developmental milestone ages
  // Data shape: { milestoneId: { age: number|null, unit: 'months'|'years', notSure: boolean, notYetDoing: boolean } }
  if (qtype === 'milestoneAgeGroup') {
    if (typeof answer === 'object') {
      const milestones = question?.milestones || [];
      const lines = milestones.map((m) => {
        const val = answer[m.milestoneId];
        if (!val) return `\u2022 ${m.label}: Not specified`;
        if (val.notYetDoing) return `\u2022 ${m.label}: Not yet doing`;
        if (val.notSure) return `\u2022 ${m.label}: Not sure`;
        if (val.age == null) return `\u2022 ${m.label}: Not specified`;
        return `\u2022 ${m.label}: ${val.age} ${val.unit || 'months'}`;
      });
      return { display: lines.join('\n'), hasAnswer: true };
    }
  }

  // For physical health measurement groups — display values with units and dates
  if (qtype === 'physicalHealthInfoGroup') {
    if (typeof answer === 'object') {
      const mt = question?.measurementType;
      const fmtDate = (obj) => {
        if (!obj) return '';
        if (obj.dateNotSure) return ' (date: not sure)';
        if (obj.date?.month && obj.date?.year) {
          const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          return ` (${months[obj.date.month - 1]} ${obj.date.year})`;
        }
        return '';
      };
      const lines = [];
      if (mt === 'height_weight') {
        const w = answer.weight || {};
        const h = answer.height || {};
        if (w.value != null) lines.push(`\u2022 Weight: ${w.value} ${w.unit || 'kg'}${fmtDate(w)}`);
        if (h.unit === 'ft_in' && (h.ft != null || h.in != null)) {
          lines.push(`\u2022 Height: ${h.ft || 0}ft ${h.in || 0}in${fmtDate(h)}`);
        } else if (h.value != null) {
          lines.push(`\u2022 Height: ${h.value} ${h.unit || 'cm'}${fmtDate(h)}`);
        }
      } else if (mt === 'head_circumference') {
        // "I don't know" stores { notSure: true } with no value \u2014 an answer,
        // not a gap (mirrors the respondent's review page).
        if (answer.notSure) lines.push("\u2022 Head circumference: I don't know");
        else if (answer.value != null) lines.push(`\u2022 Head circumference: ${answer.value} ${answer.unit || 'cm'}${fmtDate(answer)}`);
      } else if (mt === 'pulse_rate') {
        if (answer.unable) lines.push('\u2022 Unable to take measurement');
        else if (answer.value != null) lines.push(`\u2022 Pulse: ${answer.value} bpm${fmtDate(answer)}`);
      } else if (mt === 'blood_pressure') {
        if (answer.unable) lines.push('\u2022 Unable to take measurement');
        else if (answer.systolic != null || answer.diastolic != null) {
          lines.push(`\u2022 BP: ${answer.systolic ?? '-'}/${answer.diastolic ?? '-'} mmHg${fmtDate(answer)}`);
        }
      }
      return { display: lines.join('\n') || 'No data provided', hasAnswer: lines.length > 0 };
    }
  }

  // For nomination fields
  if (qtype === 'nomination') {
    if (typeof answer === 'object') {
      const children = question?.children || [];
      const lines = children.map((child) => {
        const val = answer[child.questionId];
        if (!val) return null;
        if (typeof val === 'object' && val.label) {
          return `• ${child.label}: ${val.label}`;
        }
        return `• ${child.label}: ${val}`;
      }).filter(Boolean);
      return { display: lines.join('\n') || 'Nomination data provided', hasAnswer: true };
    }
  }

  // For repeatable groups
  if (qtype === 'repeatable_group') {
    if (Array.isArray(answer)) {
      return { display: `${answer.length} entries provided`, hasAnswer: answer.length > 0 };
    }
  }

  // Default: stringify the value
  if (typeof answer === 'object') {
    return { display: JSON.stringify(answer, null, 2), hasAnswer: true };
  }

  return { display: String(answer), hasAnswer: true };
};

export default function RmsFormDetailsDialog({
  isOpen,
  onClose,
  formKey,
  formTitle,
  formInstance,
  orgId,
  role,
  initiatedBy,
  onDownload,
  downloading = false,
  downloadDisabled = false,
}) {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Immediately mark as loading when the dialog opens (or the target form changes)
  // so the previous schema doesn't flash for one render frame before the fetch starts.
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
    }
  }, [isOpen, formKey, formInstance?.schemaVersion]);

  // Fetch the schema when dialog opens
  useEffect(() => {
    if (!isOpen || !orgId || !formKey) return;

    const fetchSchema = async () => {
      setLoading(true);
      setError(null);

      try {
        const getSchema = httpsCallable(functions, 'rmsGetFormSchema');
        const schemaVersion = formInstance?.schemaVersion;

        const result = await getSchema({
          orgId,
          formKey,
          version: schemaVersion, // Use pinned version if available
        });

        if (result.data?.schema) {
          setSchema(result.data.schema);
        } else {
          setError('Failed to load form schema');
        }
      } catch (err) {
        console.error('[RmsFormDetailsDialog] Error fetching schema:', err);
        setError(err.message || 'Failed to load form schema');
      } finally {
        setLoading(false);
      }
    };

    fetchSchema();
  }, [isOpen, orgId, formKey, formInstance?.schemaVersion]);

  const answers = formInstance?.answers || {};
  const status = formInstance?.status || 'not_started';
  const progress = formInstance?.progress || {};
  const submittedAt = formInstance?.submittedAt;
  const updatedAt = formInstance?.updatedAt;

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl w-[95vw] max-w-[750px] max-h-[85vh] overflow-hidden flex flex-col z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div>
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                {formTitle || formKey}
              </Dialog.Title>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                {status === 'submitted' ? (
                  <span className="inline-flex items-center gap-1 text-green-700">
                    <CheckCircleIcon className="w-3.5 h-3.5" />
                    Submitted
                  </span>
                ) : (
                  <span>Progress: {progress.percent || 0}%</span>
                )}
                {(submittedAt || updatedAt) && (
                  <span className="inline-flex items-center gap-1">
                    <ClockIcon className="w-3 h-3" />
                    {submittedAt ? `Submitted ${formatDateTime(submittedAt)}` : `Updated ${formatDateTime(updatedAt)}`}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onDownload && (
                <button
                  type="button"
                  onClick={onDownload}
                  disabled={downloadDisabled}
                  className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border border-primary bg-white text-sm font-medium text-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloading ? (
                    <RefreshIcon className="w-4 h-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <DownloadIcon className="w-4 h-4" aria-hidden="true" />
                  )}
                  {downloading ? 'Preparing PDF…' : 'Download PDF'}
                </button>
              )}
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label="Close"
                >
                  <XIcon className="w-5 h-5" />
                </button>
              </Dialog.Close>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Loading state */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <RefreshIcon className="w-6 h-6 text-gray-400 animate-spin" />
                <span className="ml-2 text-gray-600">Loading form schema...</span>
              </div>
            )}

            {/* Error state */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                {error}
              </div>
            )}

            {/* No instance data */}
            {!formInstance && !loading && (
              <div className="text-center py-12 text-gray-500">
                No form data available. This form has not been started.
              </div>
            )}

            {/* Form content — only the sections this respondent was actually
                asked. The dual-respondent referral schemas gate sections by
                role; without the filter the OTHER respondent's questions all
                render as "No answer given". When no role is provided (legacy
                callers), every section is shown as before. */}
            {schema && !loading && formInstance && (
              <div className="space-y-6">
                {(schema.sections || [])
                  .filter((section) => !role || isSectionVisibleForRole(section, role, initiatedBy))
                  .map((section) => (
                  <div key={section.sectionId} className="space-y-4">
                    {/* Section header */}
                    <div className="border-b border-gray-200 pb-2">
                      <h3 className="text-base font-semibold text-gray-800">{section.title}</h3>
                      {section.description && (
                        <p className="text-xs text-gray-500 mt-1">{section.description}</p>
                      )}
                    </div>

                    {/* Questions in section */}
                    <div className="space-y-3">
                      {(section.questions || []).map((question) => {
                        const qid = question.questionId;
                        const rawAnswer = answers[qid];
                        const { display, hasAnswer, optionId, selectedIds } = formatAnswer(rawAnswer, question, answers);
                        const options = question.options || [];

                        return (
                          <div
                            key={qid}
                            className={`border rounded-lg p-4 ${
                              hasAnswer
                                ? 'bg-primary/5 border-primary/20'
                                : 'bg-gray-50 border-gray-200 border-dashed opacity-75'
                            }`}
                          >
                            {/* Question text */}
                            <div className="text-sm font-medium text-gray-900 mb-2">
                              {question.label}
                              {question.required && <span className="text-red-500 ml-1">*</span>}
                            </div>

                            {/* Description if present */}
                            {question.description && (
                              <p className="text-xs text-gray-500 mb-2">{question.description}</p>
                            )}

                            {/* Answer display */}
                            <div
                              className={`p-2 rounded border whitespace-pre-wrap text-sm ${
                                hasAnswer
                                  ? 'bg-white border-gray-200'
                                  : 'bg-gray-100 border-gray-200 border-dashed text-gray-500 italic'
                              }`}
                            >
                              {display}
                            </div>

                            {/* Options display for radio/checkbox */}
                            {options.length > 0 && (question.type === 'radio' || question.type === 'checkboxes') && (
                              <div className="mt-3">
                                <div className="text-xs text-gray-500 mb-1.5">Available options:</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {options.map((opt) => {
                                    const isSelected =
                                      optionId === opt.optionId ||
                                      (selectedIds && selectedIds.includes(opt.optionId));

                                    return (
                                      <span
                                        key={opt.optionId}
                                        className={`text-xs px-2 py-1 rounded ${
                                          isSelected
                                            ? 'bg-primary/10 border border-primary/30 text-primary font-medium'
                                            : 'bg-gray-100 border border-gray-200 text-gray-500'
                                        }`}
                                      >
                                        {opt.label}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Raw JSON fallback for debugging */}
                {Object.keys(answers).length > 0 && (!schema.sections || schema.sections.length === 0) && (
                  <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <div className="text-xs text-gray-400 mb-2">Raw answers (schema sections not found):</div>
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap">
                      {JSON.stringify(answers, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
